import { createHubTask, HUB_TASK_STATUS, pollTaskStatus } from '../../../../crud/integrationHub.crud'

const DEFAULT_HUB_POLL = {
  retries: 10,
  timeout: 3000,
}

export const parseHubTaskErrors = (taskErrors) => {
  if (!taskErrors) {
    return []
  }

  if (Array.isArray(taskErrors)) {
    return taskErrors
  }

  if (typeof taskErrors === 'string') {
    try {
      return parseHubTaskErrors(JSON.parse(taskErrors))
    } catch (error) {
      return [taskErrors]
    }
  }

  if (Array.isArray(taskErrors.errors)) {
    return taskErrors.errors
  }

  if (typeof taskErrors.message === 'string') {
    return [taskErrors.message]
  }

  return [taskErrors]
}

const getErrorCode = (error) => {
  if (typeof error === 'string') {
    return error
  }

  return error?.message || error?.code || ''
}

export const formatHubTaskStatusMessage = ({ errors, errorMappings, intl } = {}) => {
  const errorCode = getErrorCode(errors?.[0])
  if (!errorCode) {
    return ''
  }

  const mappedId = errorMappings?.[errorCode]
  if (mappedId && intl?.formatMessage) {
    return intl.formatMessage({ id: mappedId })
  }

  return errorCode
}

export const submitEngageHubTask = async ({
  actions,
  tasktype,
  action,
  integration,
  customerId,
  organizationId,
  userId,
  poll = DEFAULT_HUB_POLL,
} = {}) => {
  const { data: { taskId } = {} } = await createHubTask({
    actions,
    tasktype,
    action,
    integration,
    customerId,
    organizationId,
    userId,
  })

  if (!taskId) {
    throw new Error('Failed to create task')
  }

  const { data: { _source: task } = {} } = (await pollTaskStatus(taskId, poll)) ?? {}
  return task
}

export const isHubTaskFailed = (task) => !task || task.status === HUB_TASK_STATUS.FAIL

export const ENGAGE_LICENSE_ACTIONS = {
  ASSIGNED: 'ENGAGE.LICENSES_ASSIGNED',
  FAILED: 'ENGAGE.LICENSES_ASSIGN_FAILED',
}

const parseHubJsonObject = (value) => {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value
  }

  if (typeof value !== 'string' || !value) {
    return null
  }

  try {
    const parsed = JSON.parse(value)
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed
    }
  } catch {
    return null
  }

  return null
}

const licenseIdOf = (license) => (typeof license === 'string' ? license : license?.id)

/**
 * Add User marks licences assigned before the Hub task returns.
 * When the task reports which SKUs were actually applied, drop the green tick for the rest.
 */
export const reconcileGwsAddUserLicenseActions = ({
  completedActions = [],
  actionData = {},
  task,
  parameters,
} = {}) => {
  const requested = parameters?.licenseInfo?.toBeAdded
  if (!Array.isArray(requested) || requested.length === 0) {
    return { completedActions, actionData, changed: false }
  }

  const appliedLicenses = parseHubJsonObject(task?.response)?.appliedLicenses
  if (!Array.isArray(appliedLicenses)) {
    return { completedActions, actionData, changed: false }
  }

  const appliedIds = new Set(appliedLicenses)
  const requestedIds = requested.map(licenseIdOf).filter(Boolean)
  const unappliedIds = requestedIds.filter((skuId) => !appliedIds.has(skuId))
  const appliedRequestedIds = requestedIds.filter((skuId) => appliedIds.has(skuId))
  const selected = Array.isArray(actionData?.licensesAssigned) ? actionData.licensesAssigned : []
  const toLicense = (skuId) =>
    selected.find((license) => licenseIdOf(license) === skuId) || { id: skuId, displayName: skuId }

  const nextCompleted = []
  let inserted = false
  completedActions.forEach((actionId) => {
    if (actionId !== ENGAGE_LICENSE_ACTIONS.ASSIGNED && actionId !== ENGAGE_LICENSE_ACTIONS.FAILED) {
      nextCompleted.push(actionId)
      return
    }

    if (inserted) {
      return
    }

    if (appliedRequestedIds.length) {
      nextCompleted.push(ENGAGE_LICENSE_ACTIONS.ASSIGNED)
    }
    if (unappliedIds.length) {
      nextCompleted.push(ENGAGE_LICENSE_ACTIONS.FAILED)
    }
    inserted = true
  })

  if (!inserted) {
    if (appliedRequestedIds.length) {
      nextCompleted.push(ENGAGE_LICENSE_ACTIONS.ASSIGNED)
    }
    if (unappliedIds.length) {
      nextCompleted.push(ENGAGE_LICENSE_ACTIONS.FAILED)
    }
  }

  return {
    changed: true,
    completedActions: nextCompleted,
    actionData: {
      ...actionData,
      licensesAssigned: appliedRequestedIds.map(toLicense),
      licensesFailed: unappliedIds.map(toLicense),
    },
  }
}

export { DEFAULT_HUB_POLL }
